import React, { useEffect, useState } from 'react';
import { FormLayout, Input, Select } from '../../components/ui';
import { churchConfig } from '../../api/endpoints';
import type { IdNumberRow } from '../../api/endpoints';
import type { ChurchConfig } from '../../types';
import { PAGE_SIZE_OPTIONS } from '../../utils/constants';
import {
  DATE_FORMATS, DEFAULT_DATE_FORMAT, CURRENCIES, DEFAULT_CURRENCY
} from '../../utils/churchSettings';
import { formatDate, formatCurrency, todayISO } from '../../utils/format';
import type { SettingsSectionProps } from './SettingsPage';

// The parish's working rules: how dates and money print, how new IDs are
// written, how long lists are, which optional features are on, and when a
// sacrament counts as due.

/** Where each ID kind's prefix and start number live on the settings record. */
const ID_FIELDS: Record<IdNumberRow['kind'], { prefix: keyof ChurchConfig; start: keyof ChurchConfig }> = {
  FAMILY: { prefix: 'familyIdPrefix', start: 'familyIdStart' },
  MEMBER: { prefix: 'memberIdPrefix', start: 'memberIdStart' },
  MEMBERSHIP: { prefix: 'membershipNoPrefix', start: 'membershipNoStart' }
};

/** The same rule the server applies: 1-10 letters or numbers. */
const PREFIX_RE = /^[A-Z0-9]{1,10}$/;

const RulesSection = ({ form, setForm, saving, onSubmit, t }: SettingsSectionProps) => {
  const numberField = (field: 'baptismDueMonths' | 'communionDueAge' | 'confirmationDueAge') =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({
      ...form,
      [field]: e.target.value === '' ? undefined : Number(e.target.value)
    });

  // The saved formats and the number each kind will give next. Fetched again
  // after every save, so the preview reflects what the server now holds.
  const [idRows, setIdRows] = useState<IdNumberRow[] | null>(null);
  useEffect(() => {
    if (saving) return;
    churchConfig.idNumbers().then(setIdRows).catch(() => setIdRows(null));
  }, [saving]);

  return (
    <FormLayout
      title="Rules & Formats"
      submitLabel="💾 Save Rules"
      submitting={saving}
      onSubmit={onSubmit}
    >
      <FormLayout.Section title="Formats" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        Used on every screen, list and printed document. Records themselves are
        stored the same way whichever format you choose.
        Today prints as <strong style={{ color: t.textMain }}>{formatDate(todayISO())}</strong>,
        and an amount as <strong style={{ color: t.textMain }}>{formatCurrency(12500)}</strong> —
        these follow the saved choice, so they update after Save.
      </p>
      <FormLayout.Grid columns={3}>
        <Select
          label="Date format" placeholder=""
          value={form.dateFormat || DEFAULT_DATE_FORMAT}
          onChange={(e) => setForm({ ...form, dateFormat: e.target.value })}
          options={DATE_FORMATS}
        />
        <Select
          label="Currency" placeholder=""
          value={form.currencyCode || DEFAULT_CURRENCY}
          onChange={(e) => setForm({ ...form, currencyCode: e.target.value })}
          options={CURRENCIES}
        />
        <Select
          label="Rows per page in lists" placeholder=""
          value={String(form.defaultPageSize || PAGE_SIZE_OPTIONS[0])}
          onChange={(e) => setForm({ ...form, defaultPageSize: Number(e.target.value) })}
          options={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: `${n} rows` }))}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="ID Numbers" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '-8px', marginBottom: t.spacing.md }}>
        How new Family IDs, Member IDs and Membership Nos. are written: <strong>PREFIX-NUMBER</strong>,
        counting from the start number. Existing records keep the IDs they have, and a number is
        never given twice — a start number below the ones already used is kept for reference
        but numbering carries on from the last one used.
      </p>
      {(idRows || []).map((row) => {
        const fields = ID_FIELDS[row.kind];
        const typedPrefix = String(form[fields.prefix] ?? '').trim().toUpperCase();
        const prefix = typedPrefix || row.defaultPrefix;
        const typedStart = form[fields.start] as number | undefined;
        const start = typedStart ?? row.defaultStart;
        const next = Math.max(start, row.nextNumber);
        const prefixError = typedPrefix && !PREFIX_RE.test(typedPrefix)
          ? 'Use 1 to 10 letters or numbers, no spaces or symbols.' : undefined;
        return (
          <div key={row.kind} style={{ marginBottom: t.spacing.md }}>
            <FormLayout.Grid columns={3}>
              <Input
                label={`${row.label} prefix`} placeholder={row.defaultPrefix} maxLength={10}
                value={String(form[fields.prefix] ?? '')}
                onChange={(e) => setForm({ ...form, [fields.prefix]: e.target.value.toUpperCase() })}
                error={prefixError}
              />
              <Input
                label="Start from" type="number" min={1} placeholder={String(row.defaultStart)}
                value={typedStart ?? ''}
                onChange={(e) => setForm({
                  ...form, [fields.start]: e.target.value === '' ? undefined : Number(e.target.value)
                })}
              />
              <Input label="Next ID will be" value={`${prefix}-${next}`} disabled onChange={() => {}} />
            </FormLayout.Grid>
            {start < row.nextNumber && (
              <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: '4px 0 0' }}>
                Numbers up to {row.nextNumber - 1} are already used or reserved
                {row.usedUpTo ? ` (last used: ${row.usedUpTo})` : ''}, so the next {row.label} will be{' '}
                <strong style={{ color: t.textMain }}>{prefix}-{next}</strong>.
              </p>
            )}
          </div>
        );
      })}
      {!idRows && (
        <p style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>Loading the current ID numbers…</p>
      )}

      <FormLayout.Section title="Features" />
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer',
        padding: '14px', borderRadius: t.radius.lg, border: `1px solid ${t.border}`,
        backgroundColor: t.bgMain, maxWidth: '640px'
      }}>
        <input
          type="checkbox"
          // Undefined counts as on, so an existing parish keeps its uploads.
          checked={form.documentUploads !== false}
          onChange={(e) => setForm({ ...form, documentUploads: e.target.checked })}
          style={{ width: '20px', height: '20px', marginTop: '2px', cursor: 'pointer' }}
        />
        <span>
          <span style={{ fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md }}>
            Member document uploads
          </span>
          <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '3px' }}>
            When on, the member form collects certificate scans and a photo, and the
            member profile shows them. Turn off if your parish does not keep these —
            the whole Documents section is hidden everywhere.
          </span>
        </span>
      </label>

      <FormLayout.Section title="Guests" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '-8px', marginBottom: t.spacing.md }}>
        A member with status <strong>Guest</strong> is attending for a period — a posting, a
        course, a family stay — and is counted apart from the roll. When one is added, the
        stay's end date is filled in this many months ahead (it can be changed per guest).
      </p>
      <FormLayout.Grid columns={3}>
        <Input
          label="Default guest period (months)"
          type="number" min={1} placeholder="12"
          value={form.guestPeriodMonths ?? ''}
          onChange={(e) => setForm({
            ...form,
            guestPeriodMonths: e.target.value === '' ? undefined : Number(e.target.value)
          })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Opening Balance" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '-8px', marginBottom: t.spacing.md }}>
        The cash and bank balance the parish held on the day it started keeping records
        here. Financial Analytics adds every income and expense since that day to show the
        real <strong>cash position</strong>, not just the year's in-and-out. Leave blank to hide it.
      </p>
      <FormLayout.Grid columns={3}>
        <Input
          label="Opening balance"
          type="number" step="0.01" placeholder="e.g. 250000"
          value={form.openingBalance ?? ''}
          onChange={(e) => setForm({
            ...form,
            openingBalance: e.target.value === '' ? undefined : Number(e.target.value)
          })}
        />
        <Input
          label="As on date"
          type="date"
          value={form.openingBalanceDate ?? ''}
          onChange={(e) => setForm({ ...form, openingBalanceDate: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Sacrament Reminders" />
      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '-8px', marginBottom: t.spacing.md }}>
        When the Sacraments list marks a member as <strong>Due</strong> for the next stage.
        Parish practice varies, so set your own. A member whose date of birth is not on
        file is never marked due.
      </p>
      <FormLayout.Grid columns={3}>
        <Input
          label="Baptism due after (months)"
          type="number" min={0} placeholder="12"
          value={form.baptismDueMonths ?? ''}
          onChange={numberField('baptismDueMonths')}
        />
        <Input
          label="Communion due at age"
          type="number" min={0} placeholder="13"
          value={form.communionDueAge ?? ''}
          onChange={numberField('communionDueAge')}
        />
        <Input
          label="Confirmation due at age"
          type="number" min={0} placeholder="13"
          value={form.confirmationDueAge ?? ''}
          onChange={numberField('confirmationDueAge')}
        />
      </FormLayout.Grid>
    </FormLayout>
  );
};

export default RulesSection;
